/**
 * IframePreloader - Dedicated component for preloading external sites
 * Improves navigation performance by preloading iframe content
 */

import React, { useRef, useEffect, useState } from 'react'
import { CONFIG } from '../../Config/config'
import { PATHS } from '../../Config/Routing/paths'
import { authorization } from '../../Module/Auth/Auth'
import { COUNTRY_CODE } from '../../constants'

const IframePreloader = () => {
  const { client, countryCode } = authorization.getUser()
  const isUS = countryCode === COUNTRY_CODE.UNITED_STATES

  // Refs for preloading iframes
  const audienceVariableIframeRef = useRef(null)
  const [preloadedSites, setPreloadedSites] = useState(new Set())

  // Preload external sites after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUS && !preloadedSites.has('audienceVariable')) {
        const baseUrl = `${CONFIG.NEXT_BASE_URL}${PATHS.main.dashboard}${
          client ? `/${client.id}` : ''
        }${PATHS.dashboard.audienceAndVariable}`

        if (audienceVariableIframeRef.current) {
          audienceVariableIframeRef.current.src = baseUrl
          setPreloadedSites(prev => new Set([...prev, 'audienceVariable']))
        }
      }
    }, 2000) // Delay preloading to not interfere with initial page load

    return () => clearTimeout(timer)
  }, [isUS, client, preloadedSites])

  // Make iframe ref available globally for navigation
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.audienceVariableIframe = audienceVariableIframeRef.current
    }
  }, [])

  if (!isUS) {
    return null // Don't render anything if not US user
  }

  return (
    <React.Fragment>
      {/* Hidden iframe for audience variable preloading */}
      <iframe
        ref={audienceVariableIframeRef}
        style={{
          display: 'none',
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          width: '1px',
          height: '1px',
        }}
        title="Audience Variable Preload"
      />
    </React.Fragment>
  )
}

// Navigation helper that uses the preloaded iframe
export const navigateToPreloadedSite = (url, fallbackBehavior = 'window') => {
  // Check if iframe is preloaded and available
  if (window.audienceVariableIframe && window.audienceVariableIframe.src) {
    window.location.href = url
  } else {
    // Fallback behavior
    if (fallbackBehavior === 'window') {
      window.open(url, '_self')
    } else {
      window.location.href = url
    }
  }
}

export default IframePreloader
